
import { useEffect } from 'react';

const ORDER = ['banner', 'ai'] as const;

export type ChromeLayer = (typeof ORDER)[number];

export interface ChromePaint {
  top?: string | null;
  bar?: string | null;
}

const layers = new Map<ChromeLayer, ChromePaint>();
let meta: HTMLMetaElement | null = null;
let canvas: CanvasRenderingContext2D | null | undefined;

function normalize(color: string): string {
  canvas ??= document.createElement('canvas').getContext('2d', { willReadFrequently: true });
  if (!canvas) return color;
  canvas.clearRect(0, 0, 1, 1);
  canvas.fillStyle = color;
  canvas.fillRect(0, 0, 1, 1);
  const [r, g, b] = canvas.getImageData(0, 0, 1, 1).data;
  return `rgb(${r} ${g} ${b})`;
}

function resolve(key: keyof ChromePaint): string | null {
  let color: string | null = null;
  for (const layer of ORDER) color = layers.get(layer)?.[key] ?? color;
  return color;
}

function apply(): void {
  const root = document.documentElement;

  const top = resolve('top');
  if (top) {
    root.style.setProperty('--chrome-top', top);
    root.setAttribute('data-chrome-top', '');
  } else {
    root.style.removeProperty('--chrome-top');
    root.removeAttribute('data-chrome-top');
  }

  const bar = resolve('bar');
  if (!bar) {
    meta?.remove();
    meta = null;
    return;
  }
  if (!meta) {
    meta = document.createElement('meta');
    meta.name = 'theme-color';
  }
  if (!meta.isConnected) {
    document.head.insertBefore(meta, document.head.querySelector('meta[name="theme-color"][media]'));
  }
  meta.content = normalize(bar);
}

export function paintChrome(layer: ChromeLayer, paint: ChromePaint | null): void {
  if (typeof document === 'undefined') return;
  if (paint && (paint.top || paint.bar)) layers.set(layer, paint);
  else layers.delete(layer);
  apply();
}

export function useBrowserChrome(layer: ChromeLayer, top: string | null, bar: string | null): void {
  useEffect(() => {
    paintChrome(layer, { top, bar });
    return () => paintChrome(layer, null);
  }, [layer, top, bar]);
}
